/**
 *
 * Organizations/Show/Users/Table
 *
 */

import React, { memo } from 'react'
import PropTypes from 'prop-types'

// Components
import CircleImage from 'components/CircleImage'
import DeleteButton from 'components/DeleteButton'
import QueryTable from 'components/Table/Query'
import { formatPhoneNumber } from 'components/utils'

// GraphQL
import { gql, useMutation } from '@apollo/client'

const SEARCH_USERS = gql`
	query searchOrganizationUsers(
		$organization_id: ID!
		$page: Int
		$page_size: Int
		$sort: String
		$sort_direction: String
		$filters: JSON
	) {
		search: search_organization_users(
			organization_id: $organization_id
			page: $page
			page_size: $page_size
			sort: $sort
			sort_direction: $sort_direction
			filters: $filters
		) {
			_total
			results: users {
				id
				full_name
				company_name
				email
				company_email
				phone
				phone_extension
				profile_image
			}
		}
	}
`

export const REMOVE_USER = gql`
	mutation removeUserFromOrganization($organization_id: ID!, $user_id: ID!) {
		removeUserFromOrganization(input: { organization_id: $organization_id, user_id: $user_id }) {
			organization {
				id
				users {
					id
					first_name
					last_name
					full_name
					email
				}
			}
		}
	}
`

function Table({ organization_id }) {
	const [removeUser] = useMutation(REMOVE_USER)

	const columns = [
		{
			header: '',
			id: 'profile_image',
			accessor: (r) => <CircleImage url={r.profile_image} size="50px" />,
			sortable: false,
			filterable: false,
			maxWidth: 100,
		},
		{
			header: 'User Name',
			id: 'full_name',
			accessor: (r) => (
				<a target="_blank" href={`/users/preferences/${r.id}`}>
					{r.full_name}
				</a>
			),
			filter_key: 'full_name',
		},
		{
			header: 'Company Name',
			id: 'company_name',
			accessor: 'company_name',
			filter_key: 'company_name',
		},
		{
			header: 'Email',
			id: 'email',
			accessor: (r) => <a href={`mailto:${r.email}`}>{r.email}</a>,
			filter_key: 'email',
		},
		{
			header: 'Phone Number',
			id: 'phone',
			accessor: (r) => (
				<div>
					<a href={`tel:${r.phone}`}>{formatPhoneNumber(r.phone)}</a> {r.phone_extension}
				</div>
			),
			filter_key: 'phone',
			sortable: false,
		},
		{
			header: '',
			id: 'actions',
			accessor: (r) => (
				<div className="d-flex justify-content-center">
					<DeleteButton
						onDelete={() =>
							removeUser({
								variables: { organization_id, user_id: r.id },
							})
						}
					/>
				</div>
			),
			width: 100,
			sortable: false,
		},
	]

	return (
		<QueryTable
			query={SEARCH_USERS}
			columns={columns}
			variables={{ organization_id }}
			channels={[organization_id]}
			query_configs={{
				fetchPolicy: 'network-only',
			}}
		/>
	)
}

Table.propTypes = {
	organization_id: PropTypes.string.isRequired,
}

export default memo(Table)
